/**
 * <countdown-schedule>
 *
 * Controla la visibilidad de una sección/bloque de countdown según un rango de
 * fechas (activación / desactivación), interpretadas en la zona horaria de la
 * tienda. La decisión se toma en el cliente para evitar la caché de página de
 * Shopify.
 *
 * Atributos:
 *   data-activation       String del comerciante (puede ser blank). Formato
 *                         "AAAA-MM-DD HH:MM:SS" (segundos opcionales, "/" tolerado).
 *   data-deactivation     String del comerciante (puede ser blank).
 *   data-tz-offset        Offset de la tienda, p.ej. "-0300" ({{ 'now' | date: '%z' }}).
 *   data-scope            "section" => elimina el .shopify-section contenedor.
 *                         "self"    => elimina este propio wrapper (para theme blocks).
 *   data-recurring        "true"/"false". Activa la regla de horario recurrente semanal.
 *   data-recurring-days   CSV de días activos (0=Dom..6=Sáb), p.ej. "1,2,3,4,5".
 *                         Vacío => todos los días.
 *   data-recurring-start  Hora de inicio diaria "HH:MM" (24h). Vacío => desde 00:00.
 *   data-recurring-end    Hora de fin diaria "HH:MM" (24h, exclusiva). Vacío => fin del día.
 *
 * Visibilidad = (dentro del rango de fechas) Y (dentro del horario recurrente).
 * Cada compuerta abre sola si no está configurada.
 *
 * El elemento arranca con display:none inline. Si corresponde mostrarlo, se pasa
 * a display:contents (transparente al layout). Si no, se elimina del DOM.
 */
if (!customElements.get('countdown-schedule')) {
  customElements.define(
    'countdown-schedule',
    class CountdownSchedule extends HTMLElement {
      connectedCallback() {
        if (this._evaluated) return;
        this._evaluated = true;
        this.evaluate();
      }

      evaluate() {
        // En el editor de temas siempre mostrar, para que el comerciante pueda editar.
        if (window.Shopify && window.Shopify.designMode) {
          this.reveal();
          return;
        }

        const now = Date.now();

        // Se muestra solo si cumple AMBAS compuertas (rango de fechas y recurrente).
        if (this.withinDateRange(now) && this.withinRecurring(now)) {
          this.reveal();
        } else {
          this.removeTarget();
        }
      }

      /**
       * Compuerta de rango de fechas absoluto. Devuelve true si no restringe
       * (activación vacía o dato no confiable) o si "now" cae dentro del rango.
       */
      withinDateRange(now) {
        const activation = (this.getAttribute('data-activation') || '').trim();

        // Activación vacía => sin restricción de rango.
        if (!activation) return true;

        const startTs = this.parseDate(activation);

        // Activación no parseable => fail-safe: no restringe (no oculta por un typo).
        if (startTs === null) return true;

        const deactivation = (this.getAttribute('data-deactivation') || '').trim();
        const endTs = deactivation ? this.parseDate(deactivation) : null;

        // Sin fin (blank) o fin no parseable => rango abierto.
        const beforeEnd = endTs === null ? true : now <= endTs;
        return now >= startTs && beforeEnd;
      }

      /**
       * Compuerta de horario recurrente semanal (días + franja horaria diaria),
       * evaluada en la hora-pared de la tienda. Devuelve true si no restringe
       * (recurrente off, datos no confiables) o si "now" cae en un día y horario
       * activos.
       */
      withinRecurring(now) {
        // Recurrente desactivado => sin restricción.
        if (this.getAttribute('data-recurring') !== 'true') return true;

        const offsetMinutes = this.offsetToMinutes(this.getAttribute('data-tz-offset'));
        // Sin offset confiable => fail-safe: no restringe.
        if (offsetMinutes === null) return true;

        // Hora-pared de la tienda: UTC + offset, leída con getUTC*.
        const storeNow = new Date(now + offsetMinutes * 60000);
        const weekday = storeNow.getUTCDay(); // 0=Dom..6=Sáb
        const minutesOfDay = storeNow.getUTCHours() * 60 + storeNow.getUTCMinutes();

        // Días activos. Vacío => todos los días (no restringe por día).
        const days = (this.getAttribute('data-recurring-days') || '')
          .split(',')
          .map((d) => d.trim())
          .filter((d) => d !== '')
          .map(Number);
        if (days.length > 0 && !days.includes(weekday)) return false;

        // Franja horaria diaria.
        const rawStart = (this.getAttribute('data-recurring-start') || '').trim();
        const rawEnd = (this.getAttribute('data-recurring-end') || '').trim();
        const start = this.parseTime(rawStart);
        const end = this.parseTime(rawEnd);

        // Punta cargada pero inválida (typo) => fail-safe: sin restricción horaria.
        if ((rawStart && start === null) || (rawEnd && end === null)) return true;

        // Ambas vacías => sin restricción horaria (todo el día).
        if (start === null && end === null) return true;

        // Punta vacía => default documentado: inicio 00:00, fin = fin del día.
        const startMin = start === null ? 0 : start;
        const endMin = end === null ? 1440 : end;

        // fin <= inicio (incluye cruce de medianoche, no soportado) => sin restricción.
        if (endMin <= startMin) return true;

        return minutesOfDay >= startMin && minutesOfDay < endMin;
      }

      /**
       * Convierte "AAAA-MM-DD HH:MM:SS" (interpretado en la zona horaria de la
       * tienda) en un timestamp absoluto (ms). Devuelve null si el formato, los
       * rangos de calendario/reloj o el offset de la tienda no son confiables; en
       * ese caso el llamador aplica el fail-safe de mostrar.
       */
      parseDate(value) {
        const match = value.match(
          /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/
        );
        if (!match) return null;

        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);
        const hour = Number(match[4]);
        const minute = Number(match[5]);
        const second = Number(match[6] || '0');

        // Rangos válidos. Un typo (mes 13, hora 24, etc.) => null => fail-safe mostrar.
        if (
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > 31 ||
          hour > 23 ||
          minute > 59 ||
          second > 59
        ) {
          return null;
        }

        // Offset de la tienda; si no se puede determinar de forma confiable => null
        // => fail-safe mostrar (NO asumir UTC, que correría todo el horario).
        const offsetMinutes = this.offsetToMinutes(this.getAttribute('data-tz-offset'));
        if (offsetMinutes === null) return null;

        // Tratar los componentes como hora-pared en UTC y verificar que sean una fecha
        // real (rechaza fechas imposibles que V8 "rueda", p.ej. Feb 30 => Mar 2).
        const wallClockUtc = Date.UTC(year, month - 1, day, hour, minute, second);
        const check = new Date(wallClockUtc);
        if (
          check.getUTCFullYear() !== year ||
          check.getUTCMonth() !== month - 1 ||
          check.getUTCDate() !== day ||
          check.getUTCHours() !== hour ||
          check.getUTCMinutes() !== minute ||
          check.getUTCSeconds() !== second
        ) {
          return null;
        }

        // Instante real = hora-pared (como UTC) menos el offset de la tienda.
        return wallClockUtc - offsetMinutes * 60000;
      }

      /** "-0300" => -180 (minutos). Devuelve null si falta o es inválido. */
      offsetToMinutes(raw) {
        if (!raw) return null;
        const match = raw.trim().match(/^([+-])(\d{2})(\d{2})$/);
        if (!match) return null;
        const sign = match[1] === '-' ? -1 : 1;
        return sign * (Number(match[2]) * 60 + Number(match[3]));
      }

      /** "HH:MM" (24h) => minutos del día (0..1439). null si falta o es inválido. */
      parseTime(raw) {
        if (!raw) return null;
        const match = raw.trim().match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return null;
        const hour = Number(match[1]);
        const minute = Number(match[2]);
        if (hour > 23 || minute > 59) return null;
        return hour * 60 + minute;
      }

      reveal() {
        this.style.display = 'contents';
        // Forzar un re-layout para componentes que miden tamaño/posición al cargar
        // (p.ej. el countdown sticky de header), que pudieron medir mientras estaba oculto.
        const raf =
          window.requestAnimationFrame || ((cb) => window.setTimeout(cb, 16));
        raf(() => window.dispatchEvent(new Event('resize')));
      }

      removeTarget() {
        const scope = this.getAttribute('data-scope');
        let target = this;
        if (scope === 'section') {
          target = this.closest('.shopify-section') || this;
        }
        target.remove();
      }
    }
  );
}
